import type { ListBoxItemProps, PopoverProps, SelectProps } from "react-aria-components";
import {
  Autocomplete,
  Button,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as SelectBase,
  SelectValue,
  Text,
  useFilter,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { formField, type FormFieldVariant } from "../../../../styled-system/recipes";
import { FaChevronDown } from "react-icons/fa6";
import { popoverStyles } from "../styles/styles";
import { SearchInput } from "./TextField";
import { type ReactNode } from "react";

interface Props<T extends object> extends Omit<SelectProps<T>, "children" | "className"> {
  css?: SystemStyleObject;
  label?: ReactNode;
  description?: string;
  errorMessage?: string;
  items?: ReadonlyArray<T>;
  isSearchable?: boolean | "auto";
  /** Placeholder for the list's search box. */
  searchPlaceholder?: string;
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  /** Formily seems to mess up children, so this is a workaround to allow it to pass custom renderer */
  itemRenderer?: (item: T) => React.ReactNode;
  variant?: FormFieldVariant["variant"];
  popoverProps?: Pick<PopoverProps, "placement" | "shouldFlip" | "offset" | "crossOffset">;
}

export function Select<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  itemRenderer,
  variant,
  isSearchable = "auto",
  searchPlaceholder,
  isDisabled = false,
  popoverProps,
  css: cssProp,
  ...props
}: Props<T>) {
  const { t } = useTranslation(["common"]);
  const classes = formField({ variant });
  const { contains } = useFilter({ sensitivity: "base" });
  const isSearchEnabled = isSearchable === "auto" ? items && items.length > 10 : isSearchable;
  return (
    <SelectBase {...props} className={cx(classes.fieldWrapper, css(cssProp))}>
      {label && <Label className={classes.label}>{label}</Label>}
      <Button className={cx(classes.inputBox, css({ paddingY: 0 }))} isDisabled={isDisabled}>
        <SelectValue
          className={cx(
            classes.input,
            css({
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }),
          )}
        />
        <span aria-hidden="true" className={css({ fontSize: "xxsmall" })}>
          <FaChevronDown />
        </span>
      </Button>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
      <Popover
        {...popoverProps}
        className={cx(
          popoverStyles,
          css({
            width: "var(--trigger-width)",
            paddingY: "xsmall",
          }),
        )}
      >
        <Autocomplete filter={contains}>
          {isSearchEnabled && (
            <SearchInput
              // A card list gets a full, padded search field; the default inline one is too slight beside it.
              variant={variant === "card" ? undefined : "inline"}
              aria-label={t(($) => $.common.actions.Search)}
              placeholder={searchPlaceholder}
              autoFocus
              css={variant === "card" ? { margin: "small", flex: "none" } : { marginY: "xsmall", marginX: "small" }}
            />
          )}
          <ListBox
            className={css({
              paddingY: "xsmall",
              scrollbarColor: "{colors.lightgrey} transparent",
              scrollbarGutter: "stable both-edges",
              scrollbarWidth: "thin",
              overflowY: "auto",
            })}
            items={items}
          >
            {children ?? itemRenderer ?? defaultRenderer}
          </ListBox>
        </Autocomplete>
      </Popover>
    </SelectBase>
  );

  function defaultRenderer(item: object) {
    if (
      "label" in item &&
      "value" in item &&
      (typeof item.value === "string" || typeof item.value === "number" || typeof item.value === "boolean") &&
      typeof item.label === "string"
    ) {
      const isDisabled = "isDisabled" in item && item.isDisabled === true;
      // Booleans seem to work fine as values, but not officially supported
      return (
        <SelectItem id={item.value as string} textValue={item.label} isDisabled={isDisabled}>
          {item.label || "%missing_label%"}
        </SelectItem>
      );
    }
    throw new Error("You need a custom renderer");
  }
}

export function SelectItem({ className, ...props }: ListBoxItemProps & { className?: string }) {
  return (
    <ListBoxItem
      {...props}
      className={cx(
        css({
          cursor: "pointer",

          paddingY: "xsmall",
          paddingX: "medium",
          color: { base: "darkblue/80", _focus: "darkblue", _hover: "darkblue" },
          fontSize: "small",
          backgroundColor: { base: "white", _focus: "black/10", _hover: "black/10" },
          _selected: {
            color: { base: "white", _focus: "white", _hover: "white" },
            backgroundColor: { base: "trellisPalette.4/80", _focus: "trellisPalette.4", _hover: "trellisPalette.4" },
          },
          _focusVisible: { outline: "1px solid {colors.bootstrapBlue}" },
          "&[data-disabled]": {
            cursor: "not-allowed",
            color: "grey",
            backgroundColor: "white",
            opacity: 0.6,
            "&:hover, &:focus": {
              color: "grey",
              backgroundColor: "white",
            },
          },
        }),
        className,
      )}
    />
  );
}
